import { IScope, IWindowService } from "angular";
import { differenceInHours, format, max, parseJSON } from "date-fns";
import { BlockConfigurationObject } from "../../interfaces/block-configuration-object.interface";
import { MatchBet } from "../../interfaces/match-bet.interface";
import { Match } from "../../interfaces/match.interface";
import { BetService } from "../../services/bet.service";
import { GeneralService } from "../../services/general.service";
import { MatchService } from "../../services/match.service";
import { ShortlistService } from "../../services/shortlist.service";
import { SportDataService } from "../../services/sport-data.service";

export class ValueBetsController {

	_$scope: IScope;
	_$window: IWindowService;
	// _Rollbar: any;
	awayBeforeHome = false;
	betService: BetService;
	book = "all";
	bookDropdownOpen = false;
	bookOptions: Array<string> = [];
	configurationObject: BlockConfigurationObject;
	displayMode = "full";
	filterBetTypes = ["line", "total", "h2h", "other"];
	filterDropdownOpen = false;
	filterSports = [];
	filterTimeframe = 168;
	generalService: GeneralService;
	matchService: MatchService;
	shortlistService: ShortlistService;
	sportDataService: SportDataService;
	valueBetsSports: Array<string> = [];
	valuePlays: Array<MatchBet> = [];
	valuePlaysError = false;
	valuePlaysLoading = true;
	startingDisplayLimit = 20;
	currentDisplayLimit = 0;
	betSort = "date";
	
	latestLastUpdated = null;

	displayMaximumWidths = {
		"x-compact": 350,
		"compact": 500,
		"less-compact": 600,
		"medium": 700,
		"large": 800,
		"x-large": 1000,
	};


	
	/* @ngInject */
	constructor($scope: IScope, /*Rollbar: any,*/ $window: IWindowService, configurationObject: BlockConfigurationObject,
		shortlistService: ShortlistService, matchService: MatchService, sportDataService: SportDataService,
		generalService: GeneralService, betService: BetService, public cdnDomain: string) {
		
		this._$scope = $scope;
		// this._Rollbar = Rollbar;
		this._$window = $window;
		this.configurationObject = configurationObject;
		this.shortlistService = shortlistService;
		this.matchService = matchService;
		this.sportDataService = sportDataService;
		this.generalService = generalService;
		this.betService = betService;

		this.valueBetsSports = configurationObject.sports.map(s => s.toUpperCase());

		if (configurationObject.defaultBetSort) {
			this.betSort = configurationObject.defaultBetSort;
		}

		if (this.configurationObject.startingDisplayLimit) {
			this.startingDisplayLimit = this.configurationObject.startingDisplayLimit;
		}

		this.currentDisplayLimit = this.startingDisplayLimit;
		
		// By default, show plays from all sports
		this.filterSports = this.valueBetsSports;
		this.bookOptions = configurationObject.bookmakers;

		generalService.initialiseBlock();
		if (!configurationObject.matchFirstTeam || configurationObject.matchFirstTeam === "away") {
			this.awayBeforeHome = true;
		}

		Object.keys(this.displayMaximumWidths).forEach(level => {
			if ($window.document.getElementById(configurationObject.targetElementID) && $window.document.getElementById(configurationObject.targetElementID).offsetWidth <= this.displayMaximumWidths[level]) {
				this.displayMode += ` ${level}`;
			}
		});
	
		$window.addEventListener("resize", this.checkCompact);
	
		matchService.getGeoData().then(() => {
			this.retrieveValueBets();
		}, () => {
			this.retrieveValueBets();
		});
	
		document.addEventListener("click", event => {
			if ((event.target as Element).closest(".book-dropdown") === null) {
				$scope.$apply(() => {
					this.bookDropdownOpen = false;
				});
			}
	
			if ((event.target as Element).closest(".filter-dropdown") === null) {
				$scope.$apply(() => {
					this.filterDropdownOpen = false;
				});
			}
		});
	
	}
	
	setFilterTimeframe = (frame: number): void => {
		this.filterTimeframe = frame;
		this.currentDisplayLimit = this.startingDisplayLimit;
	}

	toggleBookDropdown = (): void => {
		this.bookDropdownOpen = !this.bookDropdownOpen;
	}

	toggleFilterDropdown = (): void => {
		this.filterDropdownOpen = !this.filterDropdownOpen;
	}

	showMorePicks = (): void => {
		this.currentDisplayLimit += this.startingDisplayLimit;
	}

	setBetSort = (sort: string): void => {
		this.betSort = sort;
		this.currentDisplayLimit = this.startingDisplayLimit;
	}

	checkCompact = (): void => {
		let modeString = "full";
		Object.keys(this.displayMaximumWidths).forEach(level => {
			if (this._$window.document.getElementById(this.configurationObject.targetElementID) && this._$window.document.getElementById(this.configurationObject.targetElementID).offsetWidth <= this.displayMaximumWidths[level]) {
				modeString += ` ${level}`;
			}
		});

		this._$scope.$apply(() => {
			this.displayMode = modeString;
		});
	}

	lastUpdatedString = (): string => {
		if (!this.latestLastUpdated) {
			return "Not Available";
		} else {
			return format(this.latestLastUpdated, "h:mma',' MMM d");
		}
	}
	
	retrieveValueBets = (): void => {
		this.valuePlaysLoading = true;
		this.valuePlaysError = false;
		let bookmakerList: Array<string> = [];
		if (this.configurationObject.bookmakers) {
			bookmakerList = this.configurationObject.bookmakers;
		} else if (this.configurationObject.oddsProvider || this.configurationObject.bookmakerTheme) {
			if (this.configurationObject.bookmakerThemeSportExceptions) {
				bookmakerList = [this.configurationObject.oddsProvider || this.configurationObject.bookmakerTheme, "tab"];
			} else {
				bookmakerList = [this.configurationObject.oddsProvider || this.configurationObject.bookmakerTheme];
			}
		}
		this.sportDataService.getUpcomingGames(this.valueBetsSports, false, true, bookmakerList, this.configurationObject.sportExclusiveBookmakers)
			.then((matches) => {
				matches.forEach((match: Match) => {
					match.MatchData.UserDate = parseJSON(match.MatchData.Date);
				});
				const filteredMatches: Match[] = matches
					.filter(g => this.valueBetsSports.includes(g.MatchData.Sport.toUpperCase()) && this.matchService.matchIsUpcoming(g))
					.sort((a,b) => this.matchService.gameDisplaySort(a,b));

				this.valuePlays = filteredMatches.map(match => {
					if (!match.aggregatedBestBets || match.aggregatedBestBets.length === 0) return [];

					const matchPlays = match.aggregatedBestBets.filter(bet => bet.edge > 2);
					
					matchPlays.forEach(play => play.match = match);

					return matchPlays;
				}).reduce((a, c) => a.concat(c), []);
				if (this.valuePlays.length > 0 && this.valuePlays.some(p => p.match.PreData.LastUpdated)) {
					this.latestLastUpdated = max(this.valuePlays.map(p => {
						try {
							if (p.match.PreData.LastUpdated) {
								return parseJSON(p.match.PreData.LastUpdated);
							} else {
								return 0;
							}
						} catch {
							return 0;
						}
					}));
				} else {
					this.latestLastUpdated = null;
				}


				this.valuePlaysLoading = false;
			})
			.catch((err) => {
				if (err !== "API_CALL_FAILED") {
					// this._Rollbar.error(err);
					console.error(err);
				}
				this.valuePlaysError = true;
			});
	}

	setBook = (mode: string): void => {
		this.book = mode;
		this.bookDropdownOpen = false;
	}

	toggleFilterBetType = (type: string): void => {
		if (this.filterBetTypes.includes(type)) {
			this.filterBetTypes.splice(this.filterBetTypes.indexOf(type), 1);
		} else {
			this.filterBetTypes.push(type);
		}
		this.currentDisplayLimit = this.startingDisplayLimit;
	}

	toggleAllFilterSports = (): void => {
		if (this.filterSports.length === this.valueBetsSports.length) {
			this.filterSports = [];
		} else {
			this.filterSports = this.valueBetsSports.slice();
		}
		this.currentDisplayLimit = this.startingDisplayLimit;
	}

	toggleFilterSport = (sport: string): void => {
		if (this.filterSports.includes(sport)) {
			this.filterSports.splice(this.filterSports.indexOf(sport), 1);
		} else {
			this.filterSports.push(sport);
		}
		this.currentDisplayLimit = this.startingDisplayLimit;
	}

	fitsValuePlaysFilter = (play: MatchBet): boolean => {
		if (this.book !== "all") {
			if (play.bookmaker !== this.book) {
				return false;
			}
		} else {
			// if all bookmakers is set, hide this bet if there's another one for the same match and market with a higher edge
			const similarBets = this.valuePlays.filter(p => p.match === play.match && p.type === play.type);
			if (similarBets.length > 0) {
				if (similarBets.some(otherBet => this.betOutranksBet(otherBet, play))) {
					return false;
				}
			}
		}

		if (this.filterBetTypes.length > 0) {
			if (!this.filterBetTypes.includes("other") && ["firstset"].includes(play.type)) {
				return false;
			}
			if (!this.filterBetTypes.includes(play.type)) {
				return false;
			}
		}

		// if (this.filterSports.length > 0) {
		if (!this.filterSports.includes(play.match.MatchData.Sport.toUpperCase())) {
			return false;
		}
		// }

		// check timeframe
		if (!this.matchService.matchIsUpcoming(play.match)
			|| differenceInHours(play.match.MatchData.UserDate, new Date()) > this.filterTimeframe) {
			return false;
		}

		return true;
	}

	betOutranksBet = (otherBet: MatchBet, play: MatchBet): boolean => {
		if (otherBet.edge != play.edge) {
			return otherBet.edge > play.edge;
		}

		// if it's a dead heat for best edge, only return the one whose bookmaker comes first alphabetically
		return otherBet.bookmaker > play.bookmaker;
	}

    goToLink = (link:string): void => {
        if(this.configurationObject.publicationTheme === 'ball-is-life' )
        {
            window.open(link, '_blank');
        }
    }
}